import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import { Link as RouterLink } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';

export type StageTone = 'done' | 'action' | 'idle';

type StageSlot = {
  icon: React.ReactNode;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
  tooltip?: string;
  ariaLabel?: string;
};

export type StageChipProps = {
  tone: StageTone;
  label: string;
  icon?: React.ReactNode;
  /** Primary action — click on the chip body */
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
  tooltip?: string;
  ariaLabel?: string;
  /** Optional secondary action shown as a small icon to the right of the chip body */
  aux?: StageSlot;
};

const useToneStyles = (tone: StageTone) => {
  const theme = useTheme();
  switch (tone) {
    case 'done':
      return {
        bg: theme.palette.success.light,
        fg: theme.palette.success.main,
        border: 'transparent',
        divider: alpha(theme.palette.success.main, 0.25),
        hoverBg: alpha(theme.palette.success.main, 0.18),
      };
    case 'action':
      return {
        bg: theme.palette.warning.light,
        fg: theme.palette.warning.main,
        border: 'transparent',
        divider: alpha(theme.palette.warning.main, 0.25),
        hoverBg: alpha(theme.palette.warning.main, 0.18),
      };
    case 'idle':
    default:
      return {
        bg: 'transparent',
        fg: theme.palette.text.secondary,
        border: theme.palette.divider,
        divider: theme.palette.divider,
        hoverBg: theme.palette.grey[100],
      };
  }
};

const slotComponentProps = (slot: { onClick?: () => void; to?: string; disabled?: boolean }) => {
  if (slot.disabled) return { component: 'span' as const };
  if (slot.to) return { component: RouterLink, to: slot.to } as const;
  if (slot.onClick) return { component: 'button' as const, onClick: slot.onClick };
  return { component: 'span' as const };
};

export const StageChip = ({
  tone,
  label,
  icon,
  onClick,
  to,
  disabled,
  tooltip,
  ariaLabel,
  aux,
}: StageChipProps) => {
  const tokens = useToneStyles(tone);

  const isMainInteractive = !disabled && (onClick || to);
  const isAuxInteractive = aux && !aux.disabled && (aux.onClick || aux.to);

  const mainProps = slotComponentProps({ onClick, to, disabled });
  const auxProps = aux ? slotComponentProps(aux) : null;

  const mainBody = (
    <ButtonBase
      {...mainProps}
      disabled={disabled}
      aria-label={ariaLabel ?? label}
      sx={{
        flex: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        px: 1,
        py: 0.625,
        fontSize: 11.5,
        fontWeight: 600,
        color: tokens.fg,
        background: 'transparent',
        border: 0,
        borderRadius: 0,
        cursor: isMainInteractive ? 'pointer' : 'default',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontFamily: 'inherit',
        textAlign: 'center',
        transition: 'background 0.12s ease',
        '&:hover': isMainInteractive ? { background: tokens.hoverBg } : undefined,
        '&.Mui-disabled': { color: tokens.fg, opacity: 1 },
        '& svg': { fontSize: 14 },
      }}
    >
      {icon}
      {label}
    </ButtonBase>
  );

  const mainWrapped = tooltip ? (
    <Tooltip title={tooltip} arrow>
      <Box component="span" sx={{ display: 'flex', flex: 1 }}>{mainBody}</Box>
    </Tooltip>
  ) : (
    mainBody
  );

  const auxNode = aux && auxProps ? (
    <Tooltip title={aux.tooltip ?? ''} arrow disableHoverListener={!aux.tooltip}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <ButtonBase
          {...auxProps}
          disabled={aux.disabled}
          aria-label={aux.ariaLabel ?? aux.tooltip ?? `${label} secondary action`}
          sx={{
            display: 'inline-grid',
            placeItems: 'center',
            width: 26,
            borderLeft: `1px solid ${tokens.divider}`,
            color: aux.disabled ? 'text.disabled' : tokens.fg,
            background: 'transparent',
            cursor: isAuxInteractive ? 'pointer' : 'not-allowed',
            textDecoration: 'none',
            transition: 'background 0.12s ease',
            '&:hover': isAuxInteractive ? { background: tokens.hoverBg } : undefined,
            '& svg': { fontSize: 14 },
          }}
        >
          {aux.icon}
        </ButtonBase>
      </Box>
    </Tooltip>
  ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'stretch',
        borderRadius: '8px',
        overflow: 'hidden',
        background: tokens.bg,
        border: `1px solid ${tokens.border}`,
        lineHeight: 1,
      }}
    >
      {mainWrapped}
      {auxNode}
    </Box>
  );
};
